/**
 * Audio proxy utility functions to handle CORS-blocked audio URLs
 * Shared between player-context and VideoPlayer components
 */

/**
 * Clean and proxy audio URL to handle CORS issues
 * This is the same logic used in player-context.tsx
 */
export const cleanAudioUrl = (url: string): string => {
  // Check if URL is local or already proxied to avoid double-proxying
  if (url.startsWith('/api/audio-proxy') || url.startsWith('/') || url.startsWith('blob:') || url.startsWith('data:') || url.includes('blob.vercel-storage.com')) {
    return url;
  }
  
  // 🔥 Firstory URL fix: Extract CloudFront URL from Firstory track URLs
  // Firstory track URLs often return 404, but the embedded CloudFront URLs work
  if (url.includes('m.cdn.firstory.me/track/')) {
    try {
      const urlObj = new URL(url);
      
      // Look for embedded CloudFront URL - check both encoded and unencoded forms
      let cloudFrontUrl = null;
      
      // First try to find encoded CloudFront URL
      const encodedMatch = url.match(/https%3A%2F%2F[^?&]+/);
      if (encodedMatch) {
        cloudFrontUrl = decodeURIComponent(encodedMatch[0]);
      } else {
        // Try to find unencoded CloudFront URL in the path
        const pathParts = urlObj.pathname.split('/');
        const httpsIndex = pathParts.findIndex(part => part.includes('https:'));
        if (httpsIndex !== -1) {
          // Reconstruct the CloudFront URL from path parts
          const cloudFrontParts = pathParts.slice(httpsIndex);
          cloudFrontUrl = cloudFrontParts.join('/').replace('https:/', 'https://');
        }
      }
      
      if (cloudFrontUrl) {
        // Add query string if present
        const finalCloudFrontUrl = cloudFrontUrl + urlObj.search;
        console.log('🔧 Firstory URL fix: Using CloudFront URL directly:', finalCloudFrontUrl);
        return `/api/audio-proxy?url=${encodeURIComponent(finalCloudFrontUrl)}`;
      }
    } catch (error) {
      console.warn('Failed to extract CloudFront URL from Firstory URL:', error);
    }
  }
  
  // Check if URL is from CORS whitelist (known CORS-enabled services)
  const corsWhitelist = [
    'archive.org',
    'ia800', 'ia801', 'ia802', 'ia803', 'ia804', 'ia805', 'ia806', 'ia807', 'ia808', 'ia809', // Internet Archive
  ];
  
  const urlLower = url.toLowerCase();
  const isCorsWhitelisted = corsWhitelist.some(domain => urlLower.includes(domain));
  
  if (isCorsWhitelisted) {
    console.log('✅ Using whitelisted CORS-enabled URL directly:', url);
    return url;
  }
  
  // 🔥 Universal proxy solution: All other audio URLs go through proxy
  // This avoids the maintenance burden of adding podcast hosts one by one
  console.log('🚀 Using universal audio proxy for:', url);
  return `/api/audio-proxy?url=${encodeURIComponent(url)}`;
};

/**
 * Check if a URL is likely to cause CORS issues
 */
export const willCauseCorsissue = (url: string): boolean => {
  if (!url) return false;
  
  // Local URLs, blob URLs, and data URLs don't cause CORS issues
  if (url.startsWith('/') || url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }
  
  // Already proxied URLs don't cause CORS issues
  if (url.startsWith('/api/audio-proxy')) {
    return false;
  }
  
  // Check against whitelist
  const corsWhitelist = [
    'archive.org',
    'ia800', 'ia801', 'ia802', 'ia803', 'ia804', 'ia805', 'ia806', 'ia807', 'ia808', 'ia809',
  ];
  
  const urlLower = url.toLowerCase();
  const isCorsWhitelisted = corsWhitelist.some(domain => urlLower.includes(domain));
  
  // If it's not whitelisted and is an external URL, it will likely cause CORS issues
  return !isCorsWhitelisted;
};

/**
 * Get domain from URL for debugging purposes
 */
export const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return 'invalid-url';
  }
};